/**
 * Оболочка кабинета: верхняя полоса, боковое меню, область экрана.
 *
 * Правило, ради которого этот файл выглядит именно так (app-kit.md §1):
 * **роль меняет состав экрана, а не доступность контролов.** Раздела, до
 * которого роль не дотянется, в меню нет — не серым, а вовсе. Серый контрол с
 * объяснением бывает только у временных состояний: кончилась квота, идёт замер.
 *
 * Проверяет это не список «кому что можно», написанный здесь, а матрица
 * `x-seeno-access` из контракта: у каждого раздела объявлен путь и метод, и
 * оболочка спрашивает, дотянется ли роль. Матрица порождается из того же файла,
 * из которого её применяет сервер, поэтому разъехаться они не могут.
 *
 * Тем же вопросом закрыт и **адрес**, а не только пункт меню. Спрятать раздел
 * из навигации мало: по прямой ссылке экран открывался бы целиком, и
 * наблюдатель видел бы у себя «Пригласить участника» — кнопку, которой у него
 * нет. Проверка стоит здесь, на разметке маршрутов, а не внутри экранов: экран,
 * который решает вопрос доступа сам, однажды забудут закрыть.
 *
 * Портал агентства — эти же экраны без «Семантического ядра», «Тарифа» и
 * «Участников»: у роли `agency_client` матрица их и не отдаёт.
 */

import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { Mark } from '../ui/Mark';
import { Icon } from '../ui/icons';
import { Empty } from '../ui/state';
import { ReadOnlyNotice } from '../ui/screen-states';
import { ContextSwitcher } from '../ui/ContextSwitcher';
import { Menu } from '../ui/Menu';
import { ScopeProvider } from '../scope';
import { useAccess, useSessionState } from '../session';
import { ROLE_NAMES, ROLE_SUMMARY, type Role } from '../api/access';
import { LEGAL_ENTITY } from '@shared/src/product';
import { GROUPS, SECTIONS, sectionFor } from '../sections';
import { groupTitle, sectionTitle } from '../ui/labels';
import { t } from '../i18n';
import type { Session } from '../api/auth';

/**
 * Раздел, до которого роль не дотягивается, открытый прямой ссылкой.
 *
 * Показываем причину, а не «404»: раздел существует, и человек пришёл по
 * настоящей ссылке — врать ему, что страницы нет, значит отправить его искать
 * опечатку там, где её нет. И не молчаливый перенос на обзор: тогда непонятно,
 * почему ссылка коллеги «не работает».
 */
/**
 * Сводка роли с маленькой буквы — она встаёт в середину фразы.
 *
 * Строчной делается только первая буква: сводка сама по себе фраза с точкой
 * внутри, и `toLowerCase` по всей строке ломает её вторую половину
 * («выгрузка. без изменений»).
 */
function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/**
 * Раздел, до которого роль не дотягивается.
 *
 * Две ветки, и вторая — не редкость, а состояние, из которого раньше не было
 * выхода вовсе. Человек без роли видел «Доступ открывает владелец организации»,
 * а владельцем был он сам: организации у него не завели ни на почтовом пути (там
 * поле с названием необязательное), ни на входе через Яндекс ID. Совет
 * «обратитесь к владельцу» в этом положении — тупик, потому что обращаться
 * некому.
 *
 * Организацию теперь заводит сервер на обоих путях, поэтому сюда без роли
 * попадает только приглашённый, который ещё не открыл письмо, — и ему нужен не
 * совет, а действие: перечитать сессию. Кнопка делает ровно это, а не «вернуться
 * к обзору», где его встретит та же закрытая дверь.
 */
function SectionClosed({
  title,
  role,
  onReload,
}: {
  title: string;
  role: Role | null;
  onReload: () => void;
}) {
  if (!role) {
    return (
      <Empty
        eyebrow="Вы вошли"
        title="Осталось попасть в организацию"
        action={
          <button className="btn btn-primary" type="button" onClick={onReload}>
            Проверить ещё раз
          </button>
        }
      >
        Учётная запись есть, а организации у вас пока ни одной — поэтому «{title}» и
        соседние разделы закрыты. Если вас приглашали, откройте ссылку из письма: она и
        добавит вас в организацию. Если приглашения не было, напишите на{' '}
        <a href={`mailto:${LEGAL_ENTITY.contact}`}>{LEGAL_ENTITY.contact}</a> — заведём
        организацию на вас.
      </Empty>
    );
  }
  return (
    <Empty
      eyebrow={`Ваша роль — ${lowerFirst(ROLE_NAMES[role])}`}
      title={`«${title}» открывает владелец организации`}
      action={
        <a className="btn btn-ghost" href="/">
          Вернуться к обзору бренда
        </a>
      }
    >
      {`${ROLE_SUMMARY[role]}. Этот раздел ведёт владелец организации — попросите его открыть доступ или сделать то, что вам нужно.`}
    </Empty>
  );
}

function GuardedScreen() {
  const access = useAccess();
  const { reload } = useSessionState();
  const { pathname } = useLocation();
  const section = sectionFor(pathname);

  // Адрес без раздела — это не «запрещено», а «такого нет»: его разбирает
  // маршрут `*` ниже по дереву.
  if (section && !access.reaches(section.needs.method, section.needs.path)) {
    return (
      <SectionClosed title={sectionTitle(section)} role={access.role} onReload={reload} />
    );
  }
  return <Outlet />;
}

/** Группы разделов, доступные этой роли. */
type VisibleGroups = {
  titleKey: string;
  sections: (typeof GROUPS)[number]['sections'];
}[];

/**
 * Список разделов. Один и тот же и в колонке, и в ящике — иначе на узком экране
 * у кабинета оказывается вторая, отставшая навигация.
 *
 * Иконка декоративна (`aria-hidden` внутри `Icon`), название лежит рядом и
 * прячется от глаз только в свёрнутой рейке — из дерева доступности оно не
 * уходит никогда. `title` даёт тултип по наведению; фокус и без него объявляет
 * полное название, потому что оно в самой ссылке.
 */
function SectionNav({
  groups,
  onNavigate,
}: {
  groups: VisibleGroups;
  /** Переход внутри ящика обязан его закрыть. В колонке не передаётся. */
  onNavigate?: () => void;
}) {
  return (
    <>
      {groups.map((group) => (
        <div key={group.titleKey}>
          <h2>{groupTitle(group)}</h2>
          {group.sections.map((section) => (
            <NavLink
              key={section.path}
              to={section.path}
              end={section.path === '/'}
              title={sectionTitle(section)}
              onClick={onNavigate}
            >
              <Icon name={section.icon} />
              <span className="nav-full">{sectionTitle(section)}</span>
            </NavLink>
          ))}
        </div>
      ))}
    </>
  );
}

/**
 * Ящик с разделами — ниже 960 px (app-kit.md §1).
 *
 * До него на 960, 620 и 360 px колонка превращалась в горизонтальную ленту из
 * девятнадцати ссылок, обрезанную краем экрана на шестой, без единого признака,
 * что справа есть ещё тринадцать разделов; заголовки групп при этом пропадали
 * совсем, то есть структура кабинета на мобильной ширине не показывалась вовсе.
 *
 * Сделан на родном `<dialog>` по той же причине, что и окно подтверждения: он
 * один даёт верхний слой, захват фокуса и Escape без единой строки скрипта.
 * app-kit.md §4 требует от ящика ровно модальных правил фокуса — здесь они
 * достаются из элемента, а не пишутся руками (и потому не пишутся неправильно).
 */
function Drawer({
  groups,
  open,
  onClose,
}: {
  groups: VisibleGroups;
  open: boolean;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (open && !node.open) {
      opener.current = document.activeElement;
      node.showModal();
      document.body.style.overflow = 'hidden';
    }
    if (!open && node.open) {
      node.close();
      document.body.style.overflow = '';
      // Фокус возвращается на кнопку, которая ящик открыла.
      if (opener.current instanceof HTMLElement) opener.current.focus();
    }
  }, [open]);

  useEffect(() => () => void (document.body.style.overflow = ''), []);

  return (
    <dialog
      ref={ref}
      className="drawer"
      aria-label="Разделы кабинета"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <nav className="drawer-in side">
        <SectionNav groups={groups} onNavigate={onClose} />
      </nav>
    </dialog>
  );
}

export function Shell({ session }: { session: Session }) {
  const access = useAccess();
  const { signOut } = useSessionState();
  const { pathname } = useLocation();

  /**
   * Ящик открыт — и на каком адресе его открыли.
   *
   * Состояние адресом, а не булевым флагом с эффектом на `pathname`: смена
   * адреса обязана закрывать ящик (иначе кнопка «назад» оставляет его висеть
   * поверх нового экрана), а закрывать его эффектом — это лишний каскадный
   * проход отрисовки ради того, что выводится из уже имеющихся данных.
   */
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const drawer = openedAt === pathname;

  // В меню только собранные разделы. Пункт, который ведёт на «Экран собирается»,
  // — это приглашение в тупик: пять таких пунктов обещали Портфель, Планы
  // действий, Источники, Пространства и Журнал действий и не давали ничего.
  // Маршруты при этом живы: по прямой ссылке из закладки человек по-прежнему
  // получает честное объяснение, а не пустой экран.
  const visible = GROUPS.map((group) => ({
    titleKey: group.titleKey,
    sections: group.sections.filter(
      (s) => s.ready && access.reaches(s.needs.method, s.needs.path),
    ),
  })).filter((group) => group.sections.length > 0);

  // Роли нет — значит человек ещё не в организации, и об этом ему говорит целый
  // экран. Ярлык «Роль не назначена» рядом с именем в шапке ту же новость
  // повторял пометкой из служебной таблицы, поэтому его тут просто нет.
  const role = access.role ? ROLE_NAMES[access.role] : null;

  return (
    // Смена организации перевыпускает токен и меняет всё, что под ним видно.
    // Пересоздаём область целиком, а не гасим её вручную: пережившее
    // переключение пространство — это чужой бренд на экране.
    <ScopeProvider key={session.organization?.id ?? 'none'}>
      <div className="shell">
        {/* Первая ссылка страницы — минуя меню сразу к содержимому. */}
        <a className="skip" href="#screen">
          Перейти к содержимому
        </a>

        <header className="topbar">
          {/* Кнопка ящика видна только ниже 960 px — выше него разделы стоят
              колонкой и второй вход в них не нужен. */}
          <button
            className="drawer-btn"
            type="button"
            aria-label="Разделы кабинета"
            aria-expanded={drawer}
            onClick={() => setOpenedAt(pathname)}
          >
            <Icon name="menu" size={20} />
          </button>

          <a className="brand" href="/">
            <Mark />
            seeno
          </a>

          {/* Переключатель контекста стоит первым в порядке обхода: это самый
              частый контрол продукта (app-kit.md §1). */}
          <ContextSwitcher session={session} />

          <span className="spacer" />

          <Menu
            label="Учётная запись"
            align="end"
            trigger={
              <>
                <b>{session.user.full_name || session.user.email}</b>
                {role ? (
                  <>
                    <span className="sep">·</span>
                    <span>{role}</span>
                  </>
                ) : null}
              </>
            }
            groups={[
              {
                key: 'account',
                title: session.user.email,
                items: [
                  {
                    key: 'signout',
                    label: 'Выйти',
                    onSelect: signOut,
                  },
                ],
              },
            ]}
          />
        </header>

        <nav className="side" aria-label="Разделы кабинета">
          <SectionNav groups={visible} />
        </nav>

        <Drawer groups={visible} open={drawer} onClose={() => setOpenedAt(null)} />

        <main className="screen" id="screen" tabIndex={-1}>
          {access.readOnly && role ? <ReadOnlyNotice role={role} /> : null}
          <GuardedScreen />
        </main>
      </div>
    </ScopeProvider>
  );
}

/** Раздел, который ещё не собран. Честная пустота вместо правдоподобной заглушки. */
export function NotBuiltYet() {
  const { pathname } = useLocation();
  const section = SECTIONS.find((s) => s.path === pathname);

  return (
    <Empty
      eyebrow={t('section.not-built')}
      title={section ? sectionTitle(section) : t('section.not-built')}
      action={
        <a className="btn btn-ghost" href="/">
          Вернуться к обзору бренда
        </a>
      }
    >
      {/* Было на двадцать четыре слова длиннее и объясняло не то, что человек
          спрашивает («когда будет?»), а то, почему решение принято именно
          такое. Второе предложение объясняло то же самое, только короче, —
          снято целиком: на месте отсутствующего экрана нужен ответ, а не
          позиция. */}
      Раздел ещё не собран.
    </Empty>
  );
}
